import { computed, Ref } from 'vue';
import store from '@/store';

import type { CanvasElement } from '@/typings/customilyElements/canvasElements';
import type { ElementsGroup } from '@/typings/customilyElements/elementsGroup';
import type { ElementsPageEps } from '@/typings/customilyElements/elementsPageEps';
import type { LayersProps } from '../types';

export function useElementSelection(
    props: LayersProps,
    listPageEps: Ref<ElementsPageEps[]>,
    groups: Ref<ElementsGroup[]>
) {
    const isUpdatingTree = computed(() => store.state.storeLayers?.isUpdatingTree);

    /**
     * Wait for tree updates to complete before proceeding
     */
    const waitForTreeUpdates = async (): Promise<void> => {
        if (!isUpdatingTree.value) return;

        return new Promise<void>((resolve) => {
            const checkTreeUpdate = () => {
                if (!isUpdatingTree.value) {
                    resolve();
                } else {
                    setTimeout(checkTreeUpdate, 50);
                }
            };
            checkTreeUpdate();
        });
    };

    /**
     * Set an element as active so it's marked in the layers view
     * @param elements Array of canvas elements to set as active
     */
    const setActiveElement = async (elements?: CanvasElement[]) => {
        try {
            await waitForTreeUpdates();

            if (!elements || elements.length === 0) {
                store.dispatch('storeLayers/clearSelectedItems');
                return;
            }

            const ids = elements.map((element) => element.uuid || element.id);
            store.dispatch('storeLayers/setSelectedElementByIds', { ids });
        } catch (error) {
            console.error('Error setting active element:', error);
        }
    };

    /**
     * Initialize and select the first group from pageEps
     */
    const initGroupSelectionFromPageEps = async () => {
        if (!props.canvas) return;
        props.canvas.setActiveFirstPageEps();
        props.canvas.requestRenderAll();
        props.auxCanvas?.setActiveFirstPageEps();
        props.auxCanvas?.requestRenderAll();

        await waitForTreeUpdates();

        const orderedGroups = groups.value.sort((a, b) => b.zIndex - a.zIndex);
        if (orderedGroups.length === 0) return;

        const firstEps = listPageEps.value[0];
        let groupIdToSelect: string | undefined;

        if (!firstEps) {
            groupIdToSelect = orderedGroups[0]?.uuid;
        } else {
            groupIdToSelect = orderedGroups.find((group) => group.pageEpsId === firstEps.id)?.uuid;
        }

        if (groupIdToSelect) {
            store.dispatch('storeLayers/setSelectedElementByIds', {
                ids: [groupIdToSelect]
            });
        }
    };

    const singleGroupSelection = () => {
        //
    };

    return {
        setActiveElement,
        singleGroupSelection,
        initGroupSelectionFromPageEps
    };
}
